import { Auth } from "aws-amplify"

import { createAsyncThunk } from "@reduxjs/toolkit"

import { clearStoredFarmIds, removeItem } from "./async-storage"
import { ENVIRONMENT, canSeedTestDatabase } from "./environment"
import { logger } from "./logger"

import type { TargetDatabaseName } from "@/base/types"
import type { FarmUserPermissions } from "@/constants/permissions"
import type * as Models from "@/redux/models"
import type { LoadActiveFarmResponse } from "@/redux/models"
import {
  sendRequest,
  type GetActionArgumentsType,
  type GetRequestBodyType,
} from "./send-request"

export type SeedDatabasePreset = "demo" | "no-farms"
export type VirtualDeviceType = "unconfigured" | "center_pivot_controller"

export interface SeedDatabaseOptions {
  activeFarmJoinCodeStatus: Models.FarmJoinCodeStatus | null
  activeUserIsAdmin: boolean | null
  activeUserPermissions: FarmUserPermissions
  createVirtualDevice: VirtualDeviceType | null
  markActiveFarmAsDemo: boolean | null
  presetName: SeedDatabasePreset
  termsOfServiceAccepted: boolean | null
  userPhoneNumbers: Array<
    Pick<Models.PhoneNumber, "e164" | "isVerified">
  > | null
  usesMetricSystem: boolean | null
}

export interface SeedDatabaseParams {
  authToken: string
  options: SeedDatabaseOptions
  targetDatabaseName: TargetDatabaseName
}
export interface SeedDatabaseResponse {
  actionArguments: GetActionArgumentsType<"SeedTestDatabase">
  responseData: LoadActiveFarmResponse
}

async function seedDatabase({
  authToken,
  options,
  targetDatabaseName,
}: SeedDatabaseParams): Promise<SeedDatabaseResponse> {
  if (!canSeedTestDatabase(targetDatabaseName) ) {
    throw new TypeError(
      `Cannot target ${ENVIRONMENT.targetDatabaseName} in seed database`,
    )
  }

  const body: GetRequestBodyType<"SeedTestDatabase"> & {
    [key: string]: unknown
  } = {
    actionArguments: options,
    activeFarmId: null,
    extra: {
      appName: ENVIRONMENT.appName,
      appVersion: ENVIRONMENT.appVersion,
      isEndToEndTest: ENVIRONMENT.isEndToEndTest,
      nodeEnv: process.env.NODE_ENV,
      targetDatabaseName: ENVIRONMENT.targetDatabaseName,
    },
    requestedAction: "SeedTestDatabase",
  }
  if (typeof authToken === "undefined") {
    throw new TypeError(`[seedTestDatabase.ts]: Auth token is undefined`)
  }
  try {
    // const response = await fetch(getApiUrl(ENVIRONMENT), {
    //   body: JSON.stringify(snakecaseKeys(body, { deep: true })),
    //   headers: { Authorization: authToken },
    //   method: "POST",
    // })
    //   .then((res) => {
    //     if (res.ok) {
    //       return res
    //     }

    //     throw res
    //   })
    //   .catch((error) => {
    //     logger.error(error)
    //     throw error
    //   })
    const response = await sendRequest("SeedTestDatabase", {
      actionArguments: options,
      activeFarmId: undefined,
    })

    // if (Platform.OS === "web" && shouldReloadPage) {
    //   window.location.reload()
    // }
    // const asJson = (await response.json()) as unknown

    return {
      actionArguments: body.actionArguments,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      responseData: response.farmData!,
    }
  } catch (error) {
    logger.error(error)
    throw error
  }
}

export const seedTestDatabaseAsync = createAsyncThunk<
  undefined,
  GetActionArgumentsType<"SeedTestDatabase">
>("SeedTestDatabase", async (options) => {
  try {
    if (
      !canSeedTestDatabase(ENVIRONMENT.targetDatabaseName)
    ) {
      throw new TypeError(
        `Cannot target ${ENVIRONMENT.targetDatabaseName} in seed database`,
      )
    }

    const session = await Auth.currentSession()
    const authToken = session.getIdToken().getJwtToken()
    await clearStoredFarmIds()
    await removeItem("phoneNumberPromptStatus")
    await seedDatabase({
      authToken,
      options,
      targetDatabaseName: ENVIRONMENT.targetDatabaseName,
    })
    return Promise.resolve(undefined)
  } catch (error) {
    logger.error(`Seed test Database failed`)
    logger.error(error)
    throw error
  }
})
